/*
 * Verdict language for the web-test dashboard.
 *
 * Every status in the run model maps to exactly one written label, one icon,
 * and one tone token. Status is never communicated by color alone — the icon
 * and label always travel together, and the tone only picks which of the six
 * --status-* token pairs colors them.
 */
import type {
  FailureClassification,
  RunState,
  RunStatus,
  StepStatus,
  WebTestRun
} from '@blume/web-test-model'
import {
  Ban,
  Bug,
  Circle,
  CircleCheck,
  CircleHelp,
  CircleX,
  Crosshair,
  Eye,
  FileDiff,
  LoaderCircle,
  Minus,
  Repeat2,
  ServerCrash,
  TimerOff,
  type LucideIcon
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export type Tone = 'pass' | 'fail' | 'warn' | 'info' | 'infra' | 'neutral'

export interface StatusMeta {
  label: string
  icon: LucideIcon
  tone: Tone
}

export const toneText: Record<Tone, string> = {
  pass: 'text-status-pass',
  fail: 'text-status-fail',
  warn: 'text-status-warn',
  info: 'text-status-info',
  infra: 'text-status-infra',
  neutral: 'text-status-neutral'
}

export const runStatusMeta: Record<RunStatus, StatusMeta> = {
  passed: { label: 'Passed', icon: CircleCheck, tone: 'pass' },
  failed: { label: 'Failed', icon: CircleX, tone: 'fail' },
  cancelled: { label: 'Cancelled', icon: Ban, tone: 'neutral' },
  'timed-out': { label: 'Timed out', icon: TimerOff, tone: 'warn' },
  'infrastructure-error': { label: 'Infrastructure error', icon: ServerCrash, tone: 'infra' },
  'needs-review': { label: 'Needs review', icon: Eye, tone: 'warn' }
}

/** Compact semantic badge for entity inventories; never color-only. */
export function RunStatusBadge({ meta, active = false }: { meta: StatusMeta; active?: boolean }) {
  const Icon = meta.icon
  const surface: Record<Tone, string> = {
    pass: 'border-status-pass/35 bg-status-pass-surface text-status-pass',
    fail: 'border-status-fail/35 bg-status-fail-surface text-status-fail',
    warn: 'border-status-warn/35 bg-status-warn-surface text-status-warn',
    info: 'border-status-neutral/35 bg-status-neutral-surface text-foreground',
    infra: 'border-status-infra/35 bg-status-infra-surface text-status-infra',
    neutral: 'border-status-neutral/35 bg-status-neutral-surface text-foreground'
  }
  return (
    <Badge variant="outline" className={cn('gap-1 font-medium', surface[meta.tone])}>
      <Icon aria-hidden className={cn('size-3', active && 'animate-spin')} />
      {meta.label}
    </Badge>
  )
}

export interface ClassificationMeta extends StatusMeta {
  /** One-sentence answer to "whose fault is it?" — the headline on run detail. */
  verdict: string
}

export const classificationMeta: Record<FailureClassification, ClassificationMeta> = {
  'product-failure': {
    label: 'Product failure',
    icon: Bug,
    tone: 'fail',
    verdict: 'The product broke'
  },
  'test-drift': {
    label: 'Test drift',
    icon: Crosshair,
    tone: 'warn',
    verdict: 'The test drifted — the product is likely fine'
  },
  'scenario-drift': {
    label: 'Scenario drift',
    icon: FileDiff,
    tone: 'warn',
    verdict: 'The scenario no longer matches the approved intent'
  },
  flaky: {
    label: 'Flaky',
    icon: Repeat2,
    tone: 'warn',
    verdict: 'Unstable — it passed on a clean retry'
  },
  'infrastructure-error': {
    label: 'Infrastructure error',
    icon: ServerCrash,
    tone: 'infra',
    verdict: 'The platform failed before the product could be judged'
  },
  'needs-review': {
    label: 'Needs review',
    icon: Eye,
    tone: 'warn',
    verdict: 'A human decision is required'
  },
  unknown: {
    label: 'Unclassified',
    icon: CircleHelp,
    tone: 'neutral',
    verdict: 'Not enough signal to classify this failure'
  }
}

export const runStateLabel: Record<Exclude<RunState, 'terminal'>, string> = {
  queued: 'Queued',
  preparing: 'Preparing',
  exploring: 'Exploring',
  compiling: 'Compiling',
  stabilizing: 'Stabilizing',
  running: 'Running',
  diagnosing: 'Diagnosing',
  cancelling: 'Cancelling'
}

export const stepStatusMeta: Record<StepStatus, StatusMeta> = {
  pending: { label: 'Pending', icon: Circle, tone: 'neutral' },
  running: { label: 'Running', icon: LoaderCircle, tone: 'info' },
  passed: { label: 'Passed', icon: CircleCheck, tone: 'pass' },
  failed: { label: 'Failed', icon: CircleX, tone: 'fail' },
  skipped: { label: 'Skipped', icon: Minus, tone: 'neutral' },
  cancelled: { label: 'Cancelled', icon: Ban, tone: 'neutral' }
}

/** The single sentence a run leads with: in-flight stage or terminal status. */
export function runVerdict(run: WebTestRun): StatusMeta & { active: boolean } {
  if (run.state !== 'terminal') {
    return {
      label: runStateLabel[run.state],
      icon: LoaderCircle,
      tone: 'info',
      active: true
    }
  }
  const meta = run.status ? runStatusMeta[run.status] : runStatusMeta['needs-review']
  return { ...meta, active: false }
}

/**
 * Inline status: icon + written label. Healthy and neutral outcomes render in
 * muted ink so a page of passing runs stays quiet; only failure tones ink up.
 */
export function StatusInline({
  meta,
  active = false,
  quietWhenHealthy = true,
  className
}: {
  meta: StatusMeta
  active?: boolean
  quietWhenHealthy?: boolean
  className?: string
}) {
  const Icon = meta.icon
  const quiet = quietWhenHealthy && (meta.tone === 'pass' || meta.tone === 'neutral')
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-[13px] font-medium',
        quiet ? 'text-muted-foreground' : toneText[meta.tone],
        className
      )}
    >
      <Icon aria-hidden className={cn('size-3.5 shrink-0', active && 'animate-spin')} />
      {meta.label}
    </span>
  )
}

/** Pill form, for verdict headers where the state deserves a surface. */
export function StatusBadge({ meta, active = false }: { meta: StatusMeta; active?: boolean }) {
  const Icon = meta.icon
  return (
    <Badge variant={meta.tone}>
      <Icon aria-hidden className={cn(active && 'animate-spin')} />
      {meta.label}
    </Badge>
  )
}
